// noinspection JSUnusedGlobalSymbols

import DOMPurify from "dompurify";
import { capitalize } from "lodash";

import ZorgbaseResult from "@/app/api/filter/[postcode]/ZorgbaseResult";
import { MapDisplayType } from "@/components/maps/MapDisplay";
import type {
	HAPNoordDeelnemer
} from "@/lib/models/huisartsinfo/HapNoord";
import { ObjectWithDistanceProperty } from "./models/location";

export const isServerSideRendering = typeof window === "undefined";

export function toRad(x: number) {
	return (x * Math.PI) / 180;
}

export type Point = Pick<GeolocationCoordinates, "latitude" | "longitude">;

export type Position = {
	lat: number;
	lng: number;
};

// google.maps uses shorthand for lat/lng
export function pointToPosition(p: Point): Position | undefined {
	return p.latitude && p.longitude
		? {
			lat: p.latitude,
			lng: p.longitude,
		}
		: undefined;
}

export function positionToPoint(p: Position): Point | undefined {
	return p.lat && p.lng
		? {
			latitude: p.lat,
			longitude: p.lng
		}
		: undefined;
}

export function haversineDistance(source: Point, target: Point): number {
	const dLat = Math.sin(toRad(target.latitude - source.latitude) / 2);
	const dLong = Math.sin(toRad(target.longitude - source.longitude) / 2);
	const a =
		dLat * dLat +
		Math.cos(toRad(source.latitude)) *
		Math.cos(toRad(target.latitude)) *
		dLong *
		dLong;
	const d = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return Number(d.toFixed(1));
}

export function sortByDistanceProperty(
	orgs: ObjectWithDistanceProperty[],
): ObjectWithDistanceProperty[] {
	// sort the organisations by calculated distance
	return orgs.sort((a, b) => {
		if (!a.distance || !b.distance) {
			return 0;
		}
		return a.distance - b.distance;
	});
}

export function getDeviceLocation(
	callback: (location: Position) => void,
	defaultLocation: Position,
) {
	if (navigator.geolocation) {
		navigator.geolocation.getCurrentPosition(
			(position) =>
				callback({
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				}),
			(error) => {
				console.error(`Error Code = ${error.code} - ${error.message}`);
				callback(defaultLocation);
			},
			{
				enableHighAccuracy: true,
				timeout: 20000,
				maximumAge: 0,
			},
		);
	} else {
		callback(defaultLocation);
	}
}

export function sanitizeMarkup(text?: string) {
	return {
		__html: newlineToBreak(DOMPurify.sanitize(text ?? '')),
	};
}

export function markerColorForOrganisation(organisation: ZorgbaseResult) {
	if (organisation.acceptsPatients && organisation.acceptsPassersBy) {
		return '#00ab42';
	}
	if (organisation.acceptsPatients) {
		return '#00ab42';
	}
	if (organisation.acceptsPassersBy) {
		return '#018dc6';
	}
	return '#bbb';
}

export function newlineToBreak(value: string) {
	return value.replaceAll(/\n/g, "<br/>");
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function getMapKeyForValue(map: Map<number, string>, val: any) {
	// @ts-ignore
	return [...map].find(([key, value]) => val === value)[0];
}

export function getOrganisationDisplayName(organisation: ZorgbaseResult) {
	const parts = organisation.label.split(" ");
	// biome-ignore lint/suspicious/noAssignInExpressions: need to suppress here
	parts.forEach((v, i, p) => (p[i] = capitalize(v)));
	return parts.join(" ");
}

export function isValidPostcode(postcode: string) {
	const postcodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{0,2}$/;
	return postcodeRegex.test(postcode);
}
